if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        
        // ===== CUSTOM CODE START: Filter media on initial page load =====
        // Filter images based on initially selected variant
        const variant = this.getSelectedVariant(this);
        if (variant && variant.option1) {
          // Use setTimeout to ensure DOM is fully ready
          setTimeout(() => {
            this.filterMediaByVariant(variant.option1);
          }, 100);
        }
        // ===== CUSTOM CODE END =====

        // ===== CUSTOM CODE START: Initialize variant description on page load =====
        // Update variant description on initial load
        const initialVariant = this.getSelectedVariant(this);
        if (initialVariant?.id) {
          setTimeout(() => {
            this.updateVariantDescription(initialVariant.id);
          }, 100);
        }

        // Watch for direct variant input changes (fallback for edge cases)
        const variantInput = this.productForm?.variantIdInput;
        if (variantInput) {
          // Create an observer to watch for value changes in the variant input
          this.variantDescriptionObserver = new MutationObserver(() => {
            const variantId = variantInput.value;
            if (variantId) {
              this.updateVariantDescription(variantId);
            }
          });

          // Start observing the variant input for value attribute changes
          this.variantDescriptionObserver.observe(variantInput, {
            attributes: true,
            attributeFilter: ['value']
          });

          // Also listen for change events as a backup
          variantInput.addEventListener('change', () => {
            if (variantInput.value) {
              this.updateVariantDescription(variantInput.value);
            }
          });
        }
        // ===== CUSTOM CODE END =====
        
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        
        // ===== CUSTOM CODE START: Cleanup variant description observer =====
        if (this.variantDescriptionObserver) {
          this.variantDescriptionObserver.disconnect();
        }
        // ===== CUSTOM CODE END =====
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          // ===== CUSTOM CODE START: Update variant description =====
          this.updateVariantDescription(variant?.id);
          // ===== CUSTOM CODE END =====

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // ===== CUSTOM CODE START: Filter media images based on variant option1 (Scent) =====
        // Get the selected variant to filter images
        const variant = this.getSelectedVariant(this);
        if (variant && variant.option1) {
          this.filterMediaByVariant(variant.option1);
        }
        // ===== CUSTOM CODE END =====

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      // ===== CUSTOM CODE START: Filter media images by variant option1 =====
      /**
       * Filters product media images based on the selected variant's option1 value (Scent).
       * Shows images whose alt text contains the variant name, and always shows generic images
       * (images that don't contain any variant name in their alt text).
       * 
       * @param {string} variantOption1 - The variant's option1 value (e.g., "Unscented", "Orange, Vanilla & Frankincense")
       */
      filterMediaByVariant(variantOption1) {
        if (!variantOption1) return;

        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;

        const mediaList = mediaGallery.querySelector('ul[id^="Slider-Gallery"]');
        if (!mediaList) return;

        // Get all variant option1 values from the product to identify generic images
        const allVariantOptions = this.getAllVariantOption1Values();
        
        // Get all media items (both main gallery and thumbnails)
        const mediaItems = Array.from(mediaList.querySelectorAll('li[data-media-id]'));
        const thumbnailList = mediaGallery.querySelector('ul[id^="Slider-Thumbnails"]');
        const thumbnailItems = thumbnailList ? Array.from(thumbnailList.querySelectorAll('li[data-target]')) : [];

        let firstVisibleMedia = null;
        let firstVisibleThumbnail = null;

        // Filter main gallery images
        mediaItems.forEach((mediaItem) => {
          const img = mediaItem.querySelector('img');
          if (!img) {
            // For non-image media (video, model), always show
            mediaItem.classList.remove('variant-filtered-hidden');
            if (!firstVisibleMedia) firstVisibleMedia = mediaItem;
            return;
          }

          const altText = img.getAttribute('alt') || '';
          const isGenericImage = !this.isVariantSpecificImage(altText, allVariantOptions);
          const matchesVariant = altText.toLowerCase().includes(variantOption1.toLowerCase());

          if (isGenericImage || matchesVariant) {
            // Show this image
            mediaItem.classList.remove('variant-filtered-hidden');
            if (!firstVisibleMedia) firstVisibleMedia = mediaItem;
          } else {
            // Hide this image
            mediaItem.classList.add('variant-filtered-hidden');
          }
        });

        // Filter thumbnail images
        thumbnailItems.forEach((thumbnailItem) => {
          const img = thumbnailItem.querySelector('img');
          if (!img) {
            // For non-image media, always show
            thumbnailItem.classList.remove('variant-filtered-hidden');
            if (!firstVisibleThumbnail) firstVisibleThumbnail = thumbnailItem;
            return;
          }

          const altText = img.getAttribute('alt') || '';
          const isGenericImage = !this.isVariantSpecificImage(altText, allVariantOptions);
          const matchesVariant = altText.toLowerCase().includes(variantOption1.toLowerCase());

          if (isGenericImage || matchesVariant) {
            // Show this thumbnail
            thumbnailItem.classList.remove('variant-filtered-hidden');
            if (!firstVisibleThumbnail) firstVisibleThumbnail = thumbnailItem;
          } else {
            // Hide this thumbnail
            thumbnailItem.classList.add('variant-filtered-hidden');
          }
        });

        // Set the first visible media as active if no media is currently active
        const activeMedia = mediaList.querySelector('li.is-active');
        if (!activeMedia || activeMedia.classList.contains('variant-filtered-hidden')) {
          if (firstVisibleMedia) {
            const mediaId = firstVisibleMedia.dataset.mediaId;
            mediaGallery.setActiveMedia?.(mediaId, false);
          }
        }
      }

      /**
       * Gets all variant option1 values from the product to identify which images are variant-specific
       * @returns {Array<string>} Array of all variant option1 values
       */
      getAllVariantOption1Values() {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return [];

        const optionValues = new Set();

        // Method 1: Get from first option fieldset (for swatch/button pickers)
        const firstFieldset = variantSelects.querySelector('fieldset:first-of-type');
        if (firstFieldset) {
          const inputs = firstFieldset.querySelectorAll('input[type="radio"], input[type="checkbox"]');
          inputs.forEach((input) => {
            const value = input.value?.trim();
            if (value && value !== '') {
              optionValues.add(value);
            }
          });
        }

        // Method 2: Get from first select dropdown
        const firstSelect = variantSelects.querySelector('select:first-of-type');
        if (firstSelect) {
          const options = firstSelect.querySelectorAll('option');
          options.forEach((option) => {
            const value = option.value?.trim();
            // Skip unavailable options (they usually have special text)
            if (value && value !== '' && !option.textContent.includes('unavailable')) {
              optionValues.add(value);
            }
          });
        }

        // Method 3: Fallback - try to get from data-selected-variant JSON
        if (optionValues.size === 0) {
          const variantData = variantSelects.querySelector('[data-selected-variant]');
          if (variantData) {
            try {
              const variant = JSON.parse(variantData.innerHTML);
              if (variant.option1) {
                optionValues.add(variant.option1);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }

        return Array.from(optionValues);
      }

      /**
       * Checks if an image's alt text contains any variant-specific name
       * @param {string} altText - The image's alt text
       * @param {Array<string>} variantOptions - Array of all variant option1 values
       * @returns {boolean} True if the alt text contains a variant name
       */
      isVariantSpecificImage(altText, variantOptions) {
        if (!altText || variantOptions.length === 0) return false;
        
        const lowerAltText = altText.toLowerCase();
        return variantOptions.some((variantOption) => {
          return lowerAltText.includes(variantOption.toLowerCase());
        });
      }
      // ===== CUSTOM CODE END =====

      // ===== CUSTOM CODE START: Update variant description from metafields =====
      /**
       * Updates the variant description display based on the selected variant ID.
       * Gets the metafield content from the hidden div container and displays it.
       * 
       * @param {string|number} variantId - The selected variant ID
       */
      updateVariantDescription(variantId) {
        if (!variantId) {
          // Clear description if no variant is selected
          const displayElement = document.getElementById('variant-metafield-description');
          if (displayElement) {
            displayElement.innerHTML = '';
          }
          return;
        }

        // Get the hidden variant metafields container
        const hiddenVariantMetafields = document.getElementById('hidden-variant-metafields');
        if (!hiddenVariantMetafields) return;

        // Find the div with matching variant ID (using value attribute)
        const variantDiv = hiddenVariantMetafields.querySelector(`div[value="${variantId}"]`);
        if (!variantDiv) return;

        // Get the display element
        const displayElement = document.getElementById('variant-metafield-description');
        if (!displayElement) return;

        // Clone the content from the hidden div to preserve HTML structure
        // The metafield_tag outputs HTML, so we need to preserve it
        displayElement.innerHTML = variantDiv.innerHTML;
      }
      // ===== CUSTOM CODE END =====
    }
  );
}
